import { CellContext } from '@tanstack/react-table';
import { getTableMeta, PurchaseOrderRow } from '../../helpers';
import { Skeleton } from '@round/ui-kit';
import { Button } from '@round/whitelabel-ui';
import { useCreateInvoice } from '@round/creatorbase-helpers/api-hooks';
import { useState } from 'react';
import { Modal } from '@round/whitelabel-ui';
import { showNotification } from 'helpers';
import styles from './InvoiceCell.module.css';
import { creatorbase } from '@round/api';
import cn from 'classnames';
import { isEmptyObject } from 'utility/utility';

type Context = CellContext<PurchaseOrderRow, any>;

const invoiceStatusLabels: Record<creatorbase.InvoiceStatus, string> = {
    PAID: 'Invoice Paid',
    CANCELLED: 'Cancelled',
    CREATED: 'Invoice Created',
};

const InvoiceCell = ({ table, row: { original } }: Context) => {
    const { isLoading, refetch } = getTableMeta(table);
    const { create, helpers, isLoading: isInvoiceCreateLoading } = useCreateInvoice();
    const isInvoiceCreateDisabled = !original.number;
    const [isConfirmCreateOpen, setIsConfirmCreateOpen] = useState(false);

    if (isLoading && isEmptyObject(original)) {
        return <Skeleton width="7rem" />;
    }

    if (original.invoice_status) {
        return (
            <div className={cn(styles.badge, styles[original.invoice_status.toLowerCase()])}>
                <b>{original.invoice_number ? original.invoice_number : null}</b>
                <span>{invoiceStatusLabels[original.invoice_status]}</span>
            </div>
        );
    }

    return (
        <>
            <Button
                appearance="primary"
                type="button"
                disabled={isInvoiceCreateDisabled}
                onClick={() => setIsConfirmCreateOpen(true)}
            >
                Submit
            </Button>

            <Modal
                closeOnEscape
                closeOnOverlayClick
                isOpen={isConfirmCreateOpen}
                onClose={() => setIsConfirmCreateOpen(false)}
            >
                <Modal.Header>
                    <Modal.Title>Confirm Invoice Submit</Modal.Title>
                </Modal.Header>
                <Modal.Body className={styles.body}>
                    You will not be able to edit or delete Purchase Order after submitting an invoice.
                </Modal.Body>
                <Modal.Actions className={styles.actions}>
                    <Button appearance="ghost" type="button" onClick={() => setIsConfirmCreateOpen(false)}>
                        Cancel
                    </Button>
                    <Button
                        appearance="primary"
                        type="button"
                        isLoading={isInvoiceCreateLoading}
                        onClick={() =>
                            create(
                                { purchase_order_id: original.id },
                                {
                                    onSuccess: () => {
                                        refetch();
                                        showNotification('Success', 'info');
                                    },
                                    onError: (error) => {
                                        const errorMessage = helpers.getErrorMessage(error);
                                        showNotification(errorMessage, 'error');
                                    },
                                    onSettled: () => {
                                        setIsConfirmCreateOpen(false);
                                    },
                                }
                            )
                        }
                    >
                        Submit
                    </Button>
                </Modal.Actions>
            </Modal>
        </>
    );
};

export default InvoiceCell;
